import {
  Box,
  Grid,
  styled,
  Tooltip,
  Typography,
  TypographyProps,
} from '@mui/material';
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { teal2 } from '../../../../../theme';
import { FactboxFontSizes } from './types';

// Styled components
export const FactboxContainer = styled(Box)`
  border-radius: 4px;
  padding: 0.5em;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: ${teal2};
`;

export const FactboxTextBase = styled(Typography)`
  color: #ffffff;
  white-space: pre;
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
`;

export const FactboxText = (props: TypographyProps) => {
  const ref = useRef<HTMLElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const tooltipText =
    typeof props.children === 'string' ? props.children : undefined;

  useEffect(() => {
    const checkOverflow = () => {
      if (ref.current) {
        const { scrollWidth, clientWidth, scrollHeight, clientHeight } =
          ref.current;
        setIsOverflowing(
          scrollWidth > clientWidth || scrollHeight > clientHeight
        );
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [props.children]);

  return tooltipText && isOverflowing ? (
    <Tooltip title={tooltipText}>
      <FactboxTextBase ref={ref} {...props} />
    </Tooltip>
  ) : (
    <FactboxTextBase ref={ref} {...props} />
  );
};

export const FactboxTitle = styled(FactboxText)`
  font-size: 1em;
  white-space: normal;
`;

export const FactboxSubtitle = styled(FactboxText)`
  font-size: 0.8em;
`;

export const SingleColumnMainValue = styled(FactboxText)`
  font-size: 1.5em;
`;

export const SingleColumnSubValue = styled(FactboxText)`
  font-size: 0.8em;
`;

export const StoryboardFooter = styled(FactboxText)`
  font-size: 0.8em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
`;

interface FactboxBaseProps {
  title?: string;
  subtitle?: string;
  footer?: string;
  fontSize?: FactboxFontSizes;
}

export const FactboxBase = (props: PropsWithChildren<FactboxBaseProps>) => {
  const { title, subtitle, footer, children, fontSize } = props;

  return (
    <FactboxContainer
      sx={{ fontSize: fontSize === FactboxFontSizes.SMALL ? '1rem' : '1.3rem' }}
    >
      <Grid
        container
        direction="column"
        sx={{ height: '100%', width: '100%' }}
        wrap="nowrap"
      >
        {/* Header section */}
        {(title || subtitle) && (
          <Grid item>
            {title && <FactboxTitle>{title}</FactboxTitle>}
            {subtitle && <FactboxSubtitle>{subtitle}</FactboxSubtitle>}
          </Grid>
        )}

        {/* Content section */}
        <Grid item container alignItems={'center'} sx={{ flexGrow: 1 }}>
          {children}
        </Grid>

        {/* Footer section */}
        {footer && (
          <Grid item>
            <StoryboardFooter>{footer}</StoryboardFooter>
          </Grid>
        )}
      </Grid>
    </FactboxContainer>
  );
};
